import React from 'react';
import { animated, useSpring } from '@react-spring/web';
import { Button } from '@pushpay/button';
import { ExpandItem, ExpandPanel } from '@pushpay/expand';
import { ArrowSolidIcon } from '@pushpay/iconography';
import { Link } from '@pushpay/link';
import { clsx } from '@pushpay/styles';
import Tooltip from '@pushpay/tooltip';
import { useScrollIntoViewOnMount } from '@pushpay/utils';
import { DisplayStyleListener } from './display-style-listener';
import { useIsActive, useIsGroupActive } from './helpers';
import { NavigationFlyout, useNavigationFlyout } from './navigation-flyout';
import { NavigationItem } from './navigation-item';
import { useNavigationStyles } from './navigation-styles';
import { useNavigation } from './use-navigation';

function NavigationItemGroupLink({
  label,
  classes: classesProp,
  'aria-hidden': ariaHidden,
  onItemClick,
  end,
  ...rest
}) {
  var _rest$to;

  const classes = useNavigationStyles(classesProp);
  const isActive = useIsActive((_rest$to = rest.to) !== null && _rest$to !== void 0 ? _rest$to : rest.href);
  const {
    navContainerRef
  } = useNavigation();
  const activeItemScrollRef = useScrollIntoViewOnMount({
    container: navContainerRef,
    behavior: 'auto'
  });
  return /*#__PURE__*/React.createElement("li", {
    key: label,
    ref: isActive ? activeItemScrollRef : undefined,
    className: classes.listItem
  }, /*#__PURE__*/React.createElement(Link, Object.assign({
    end: end,
    tabIndex: isActive && !ariaHidden ? 0 : -1,
    "aria-hidden": ariaHidden,
    "data-autofocus": isActive,
    displayStyle: "unstyled",
    onClick: onItemClick,
    className: classes.item,
    classes: {
      root: classes.item,
      active: classes.itemActive
    }
  }, rest), label));
}

function NavigationItemGroupChildren({
  groupId,
  children,
  'aria-hidden': ariaHidden,
  classes: classesProp,
  className: classNameProp,
  onItemClick,
  ...rest
}) {
  const classes = useNavigationStyles(classesProp);
  const className = clsx(classes.list, classNameProp);
  return /*#__PURE__*/React.createElement("ul", Object.assign({
    id: groupId,
    role: "group",
    "aria-hidden": ariaHidden,
    className: className
  }, rest), children.map(childItem => /*#__PURE__*/React.createElement(NavigationItemGroupLink, Object.assign({
    key: childItem.label,
    classes: classes,
    onItemClick: onItemClick,
    "aria-hidden": ariaHidden
  }, childItem))));
}

const AnimatedArrowSolidIcon = animated(ArrowSolidIcon);

function NavigationItemGroupArrow({
  expandStatus,
  displayStyle,
  classes: classesProp,
  className: classNameProp
}) {
  const classes = useNavigationStyles(classesProp);
  const className = clsx(classes.itemGroupArrow, classNameProp);
  const style = useSpring({
    opacity: displayStyle === 'collapsed' ? 0 : 1,
    delay: displayStyle === 'collapsed' ? 0 : 150
  });
  return /*#__PURE__*/React.createElement(AnimatedArrowSolidIcon, {
    className: className,
    style: style,
    mode: expandStatus === 'collapsed' ? 'down' : 'up'
  });
}

export function NavigationItemGroup({
  icon: ItemIcon,
  indicatorIcon: IndicatorIcon,
  indicatorIconTooltip,
  label,
  navigationId,
  'data-pp-at-target': targetId,
  displayStyle,
  scrolling,
  children,
  isActive,
  classes: classesProp
}) {
  const groupId = `${navigationId}-${label}-group`;
  const classes = useNavigationStyles(classesProp);
  const {
    flyoutOpen,
    toggleFlyout,
    buttonRef,
    flyoutRef
  } = useNavigationFlyout();
  const isGroupActive = useIsGroupActive(children, isActive);
  return /*#__PURE__*/React.createElement(ExpandItem, {
    className: classes.itemGroup,
    initialExpandStatus: isGroupActive && displayStyle === 'expanded' ? 'preexpanded' : 'collapsed'
  }, (expandStatus, toggleExpandStatus) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DisplayStyleListener, {
    displayStyle: displayStyle,
    expandStatus: expandStatus,
    isActive: isGroupActive,
    toggleExpandStatus: toggleExpandStatus
  }), /*#__PURE__*/React.createElement(Tooltip, {
    className: classes.tooltip,
    disabled: displayStyle === 'expanded' || scrolling || flyoutOpen,
    placement: "right",
    content: label,
    panelSpace: "XXSMALL"
  }, /*#__PURE__*/React.createElement(Button, {
    ref: buttonRef,
    type: "button",
    tabIndex: isGroupActive && expandStatus === 'collapsed' ? 0 : -1,
    "aria-owns": groupId,
    "aria-expanded": expandStatus !== 'collapsed',
    onClick: displayStyle === 'expanded' ? toggleExpandStatus : toggleFlyout,
    className: clsx(classes.item, isGroupActive && classes.itemActive, flyoutOpen && classes.itemOpen),
    "data-pp-at-target": targetId
  }, /*#__PURE__*/React.createElement(NavigationItem, {
    label: label,
    icon: ItemIcon,
    displayStyle: displayStyle,
    indicatorIcon: IndicatorIcon,
    indicatorIconTooltip: indicatorIconTooltip
  }), /*#__PURE__*/React.createElement(NavigationItemGroupArrow, {
    displayStyle: displayStyle,
    expandStatus: expandStatus
  }))), /*#__PURE__*/React.createElement(ExpandPanel, {
    expandStatus: expandStatus
  }, /*#__PURE__*/React.createElement(NavigationItemGroupChildren, {
    groupId: groupId,
    "aria-hidden": expandStatus === 'collapsed',
    classes: {
      list: classes.childExpandList,
      item: clsx(classes.item, classes.childExpandItem),
      itemActive: clsx(classes.itemActive, classes.childExpandItemActive)
    }
  }, children)), flyoutOpen && /*#__PURE__*/React.createElement(NavigationFlyout, {
    ref: flyoutRef,
    anchorElement: buttonRef,
    toggleFlyout: toggleFlyout
  }, /*#__PURE__*/React.createElement(NavigationItemGroupChildren, {
    groupId: groupId,
    onItemClick: toggleFlyout,
    classes: {
      item: clsx(classes.item, classes.childFlyoutItem),
      itemActive: clsx(classes.itemActive, classes.childFlyoutItemActive)
    }
  }, children))));
}